import React, { useState, useEffect } from "react";
import "../../styles/index.css";
import {
  Box,
  Button,
  TextField,
  InputAdornment,
  Dialog,
  DialogContent,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import PersonAddAltOutlinedIcon from "@mui/icons-material/PersonAddAltOutlined";
import FileUploadOutlinedIcon from "@mui/icons-material/FileUploadOutlined";
import TuneIcon from "@mui/icons-material/Tune";
import BASE_URL from "../config/appconfig";
import Contacts from "./contacts";
import NewUsers from "./newUsers";
import AddContactDialog from "./addContactDialog";
import UploadContactsDialog from "./uploadContactsDialog";
import FieldConfigManager from "./fieldConfigManager";
import SnackbarAlert from "../snackbarAlert/snackbarAlert";

const UserList = ({ onDeleteSuccess }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [data, setData] = useState([]);
  const [fieldConfigs, setFieldConfigs] = useState([]);
  const [activeListTab, setActiveListTab] = useState("contacts");
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedContact, setSelectedContact] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [contactToDelete, setContactToDelete] = useState(null);
  const [dialogMode, setDialogMode] = useState(" ");
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [fieldConfigOpen, setFieldConfigOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const [newUsers, setNewUsers] = useState([]);

  const handlePaymentStatusChange = async (id, newStatus) => {
    try {
      const res = await fetch(`${BASE_URL}/api/people/update/${id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ payment_status: newStatus }),
      });
      const result = await res.json();
      if (result.success) {
        setSnackbar({ open: true, message: "Payment status updated", severity: "success" });
        fetchUsers();
      } else {
        setSnackbar({ open: true, message: result.message || "Update failed", severity: "error" });
      }
    } catch {
      setSnackbar({ open: true, message: "Failed to update payment status", severity: "error" });
    }
  };

  const fetchUsers = async () => {
    try {
      const res = await fetch(`${BASE_URL}/api/people`);
      const result = await res.json();

      const formatted = result.data.map((user) => ({
        id: user._id,
        name: user.full_name,
        email: user.email,
        church: user.church_name,
        age: user.church_age,
        location: `${user.city}, ${user.country}`,
        participated: user.participated_ctc_training ? "Yes" : "No",
        connected: user.connected_ctc_leader ? "Yes" : "No",
        updates: user.wants_contact ? "Yes" : "No",
        payment_status:
          user.payment_status.charAt(0).toUpperCase() +
          user.payment_status.slice(1),
        is_admin: user.is_admin,
        role: user.roles?.name || "Unknown",
      }));
      setNewUsers(formatted);
    } catch (error) {
      console.error("Error fetching users", error);
    }
  };

  const refreshAll = () => {
    fetchContacts();
    fetchUsers();
  };

  const handleOpenDialog = () => {
    setSelectedContact(null);
    setOpenDialog(true);
    setDialogMode("add");
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedContact(null);
  };

  const confirmDelete = async () => {
    try {
      let url = "";

      if (activeListTab === "contacts") {
        url = `${BASE_URL}/api/contact-list/delete/${contactToDelete}`;
      } else if (activeListTab === "new-users") {
        url = `${BASE_URL}/api/people/delete-complete/${contactToDelete}`;
      }

      await fetch(url, {
        method: "DELETE",
      });

      fetchContacts();
      fetchUsers();
      setDeleteDialogOpen(false);
      setContactToDelete(null);
      onDeleteSuccess?.();
    } catch (error) {
      console.error(error);
    }
  };

  const fetchContacts = async () => {
    try {
      const response = await fetch(`${BASE_URL}/api/contact-list`);
      const result = await response.json();
      if (result.success) setData(result.data);
    } catch (error) {
      console.error("Error fetching contacts:", error);
    }
  };

  const fetchFieldConfigs = async () => {
    try {
      const res = await fetch(`${BASE_URL}/api/field-config`);
      const result = await res.json();
      if (result.success) setFieldConfigs(result.data);
    } catch {
      // silently ignore
    }
  };

  const handleEdit = (contact) => {
    setSelectedContact(contact);
    setDialogMode("edit");
    setOpenDialog(true);
  };
  const handlePreview = (user) => {
    const transformedUser = {
      _id: user.id,
      full_name: user.name,
      email: user.email,
      church_name: user.church,
      church_age: user.age,
      city: user.location.split(",")[0],
      country: user.location.split(",")[1],
      is_admin: user.is_admin || false,
    };

    setSelectedContact(transformedUser);
    setDialogMode("preview");
    setOpenDialog(true);
  };
  const handleDelete = (id) => {
    setContactToDelete(id);
    setDeleteDialogOpen(true);
  };

  useEffect(() => {
    fetchContacts();
    fetchUsers();
    fetchFieldConfigs();
  }, []);

  return (
    <Box className="user-list-container">
      {/* Main Card with Inner Tabs */}
      <Box className="table-card list-inner-card">
        {/* Inner Tabs header */}
        <Box
          sx={{
            display: "flex",
            flexDirection: isMobile ? "column" : "row",
            alignItems: isMobile ? "stretch" : "center",
            justifyContent: "space-between",
            borderBottom: "1px solid #e2e8f0",
          }}
        >
          {/* Tab row */}
          <Box className="list-inner-tabs">
            <Button
              className={`inner-tab-btn ${activeListTab === "contacts" ? "inner-tab-active" : "inner-tab-inactive"}`}
              onClick={() => { setActiveListTab("contacts"); setSearchQuery(""); }}
            >
              Contacts
            </Button>
            <Button
              className={`inner-tab-btn ${activeListTab === "new-users" ? "inner-tab-active" : "inner-tab-inactive"}`}
              onClick={() => { setActiveListTab("new-users"); setSearchQuery(""); }}
            >
              New Users
            </Button>
          </Box>

          {/* Search + action buttons (contacts tab only) */}
          {activeListTab === "contacts" && (
            isMobile ? (
              /* Mobile: stacked column — search full width, then buttons row */
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "8px",
                  p: "12px 16px",
                  borderTop: "1px solid #e2e8f0",
                }}
              >
                <TextField
                  placeholder="Search"
                  size="small"
                  className="tab-search-box"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  sx={{ width: "100%" }}
                  slotProps={{
                    input: {
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon fontSize="small" sx={{ color: "#8b95a1" }} />
                        </InputAdornment>
                      ),
                    },
                  }}
                />
                <Box sx={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
                  <Button variant="outlined" className="tab-action-btn" startIcon={<PersonAddAltOutlinedIcon sx={{ fontSize: 15 }} />} onClick={handleOpenDialog}>
                    Add Contact
                  </Button>
                  <Button variant="outlined" className="tab-action-btn" startIcon={<FileUploadOutlinedIcon sx={{ fontSize: 15 }} />} onClick={() => setUploadDialogOpen(true)}>
                    Upload List
                  </Button>
                  <Button variant="outlined" className="tab-action-btn" startIcon={<TuneIcon sx={{ fontSize: 15 }} />} onClick={() => setFieldConfigOpen(true)}>
                    Custom Fields
                  </Button>
                </Box>
              </Box>
            ) : (
              /* Desktop: inline row — search + buttons on the right */
              <Box sx={{ display: "flex", gap: "8px", alignItems: "center", pr: "16px" }}>
                <TextField
                  placeholder="Search"
                  size="small"
                  className="tab-search-box"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  sx={{ width: 180 }}
                  slotProps={{
                    input: {
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon fontSize="small" sx={{ color: "#8b95a1" }} />
                        </InputAdornment>
                      ),
                    },
                  }}
                />
                <Button variant="outlined" className="tab-action-btn" startIcon={<PersonAddAltOutlinedIcon sx={{ fontSize: 15 }} />} onClick={handleOpenDialog}>
                  Add Contact
                </Button>
                <Button variant="outlined" className="tab-action-btn" startIcon={<FileUploadOutlinedIcon sx={{ fontSize: 15 }} />} onClick={() => setUploadDialogOpen(true)}>
                  Upload List
                </Button>
                <Button variant="outlined" className="tab-action-btn" startIcon={<TuneIcon sx={{ fontSize: 15 }} />} onClick={() => setFieldConfigOpen(true)}>
                  Custom Fields
                </Button>
              </Box>
            )
          )}
        </Box>

        {/* Dynamic Content */}
        {activeListTab === "contacts" && (
          <Contacts
            data={data}
            onEdit={handleEdit}
            onDelete={handleDelete}
            searchQuery={searchQuery}
            fieldConfigs={fieldConfigs}
          />
        )}
        {activeListTab === "new-users" && (
          <NewUsers
            data={newUsers}
            onEdit={handlePreview}
            onDelete={handleDelete}
            onPaymentStatusChange={handlePaymentStatusChange}
            searchQuery={searchQuery}
          />
        )}
      </Box>
      <UploadContactsDialog
        open={uploadDialogOpen}
        handleClose={() => setUploadDialogOpen(false)}
        refreshContacts={refreshAll}
        showSnackbar={(message, severity) =>
          setSnackbar({ open: true, message, severity })
        }
      />
      <FieldConfigManager
        open={fieldConfigOpen}
        handleClose={() => setFieldConfigOpen(false)}
        onRefresh={fetchFieldConfigs}
        showSnackbar={(message, severity) =>
          setSnackbar({ open: true, message, severity })
        }
      />
      <AddContactDialog
        open={openDialog}
        handleClose={handleCloseDialog}
        refreshContacts={refreshAll}
        selectedContact={selectedContact}
        mode={dialogMode}
        fieldConfigs={fieldConfigs}
        showSnackbar={(message, severity) =>
          setSnackbar({ open: true, message, severity })
        }
      />

      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        maxWidth="xs"
        fullWidth
        slotProps={{
          paper: { className: "editor-dialog-paper" },
        }}
      >
        <DialogContent sx={{ textAlign: "center", p: { xs: 2, sm: 4 } }}>
          <Typography fontWeight={600} mb={2} sx={{ fontSize: { xs: "15px", sm: "20px" } }}>
            {activeListTab === "contacts" ? "Delete Contact" : "Delete User"}
          </Typography>
          {activeListTab === "contacts" ? (
            <Typography sx={{ color: "#6b7280", mb: 4, fontSize: { xs: "12px", sm: "14px" } }}>
              Are you sure you want to delete this contact? This action cannot
              be undone.
            </Typography>
          ) : (
            <Typography sx={{ color: "#6b7280", mb: 4, fontSize: { xs: "12px", sm: "14px" } }}>
              Are you sure you want to delete this user? This action cannot be
              undone.
            </Typography>
          )}

          <Box display="flex" justifyContent="center" gap={2}>
            <Button
              variant="outlined"
              onClick={() => setDeleteDialogOpen(false)}
              sx={{ fontSize: { xs: "11px", sm: "14px" }, padding: { xs: "4px 12px", sm: "6px 16px" } }}
            >
              Cancel
            </Button>

            <Button
              variant="contained"
              sx={{
                backgroundColor: "#ef2d2d",
                "&:hover": { backgroundColor: "#d91c1c" },
                fontSize: { xs: "11px", sm: "14px" },
                padding: { xs: "4px 12px", sm: "6px 16px" },
              }}
              onClick={confirmDelete}
            >
              Delete
            </Button>
          </Box>
        </DialogContent>
      </Dialog>
      <SnackbarAlert
        open={snackbar.open}
        message={snackbar.message}
        severity={snackbar.severity}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      />
    </Box>
  );
};

export default UserList;
